// Catalog.ID Client — Organization workspace, *Wallets*
// ============================================================================
// The organization's own wallet, seen from a delegate's session
// (registration-wallets §4.13, D14): its balance, the address people pay it at,
// the spending permissions services hold on it, and the deliberate per-delegate
// tokens that make organization work attributable on the ledger.
//
// ── the custody posture, and why this pane never asks for a supersecret ─────
// The organization's wallet key is an ENTITLED ORG KEY for the owner and the
// treasurer, wrapped to each of their `attr_encrypt` keys like every other org
// key. A PIN-unlocked session of such a delegate therefore reaches it through
// the wraps alone — the posture every org key already has, and the reason the
// organization's default token has a device cache slot of its own. An admin or
// a viewer holds no wrap, so this pane does not ask them for anything: they get
// one line naming the two roles that do.
//
// ── the one thing this pane cannot do ───────────────────────────────────────
// It cannot PUBLISH the organization's receive address. Publication is scoped
// to the signed-in account server-side (`PublicClaimController.Publish` matches
// `claim.AccountId` against `session.AccountId`, and reads the publishing
// identity's kind from the session), so a delegate publishing a claim the
// ORGANIZATION owns is refused — there is no organization-scoped publish route,
// and the organization account itself has no session (docs/cid-governance.md
// M15), so nothing can publish it today. The wallet card says so where a member
// would otherwise look for the button.

import { createElement, createFieldGroup } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext } from '@catalog-shell';
import { identity } from '../state/identity';
import { PANE_IDS } from '../shell-config';
import { getOrgWalletKey } from '../key-grant/bitcash-wallet-access';
import { orgDefaultTokenSlot, getDefaultTokenCache } from '../key-grant/bitcash-token-cache';
import { toCompact } from '../crypto/bitcash';
import { canUseOrgWallet, mintDelegateToken, type WalletSlot } from './bitcash-wallets-core';
import { liveWalletsWorker, renderWalletCard } from './bitcash-wallets-group';

/** The single line every other delegate sees. */
export const ORG_WALLET_DENIED_LINE =
    'The organization\'s wallet is held by its owner and its treasurer. Your role does not carry '
    + 'the organization\'s wallet key, so there is nothing here for this session — ask an owner or '
    + 'the treasurer if the organization needs to pay for something.';

export type OrgWalletsPlan =
    | { kind: 'wallet'; orgCid: string }
    | { kind: 'denied'; message: string }
    | { kind: 'no-organization' };

/**
 * What this pane shows for the active identity. Pure, so the gate that decides
 * whether a delegate sees the organization's money is a unit test rather than a
 * screenshot.
 */
export function planOrgWalletsView(who: {
    getOrganizationCid(): string | null;
    getRoles(): string[];
}): OrgWalletsPlan {
    const orgCid = who.getOrganizationCid();
    if (!orgCid) return { kind: 'no-organization' };
    if (!canUseOrgWallet(who.getRoles())) {
        return { kind: 'denied', message: ORG_WALLET_DENIED_LINE };
    }
    return { kind: 'wallet', orgCid };
}

export function createOrgWalletsPane(): PaneType {
    return {
        typeId: PANE_IDS.orgWallets,
        kind: 'leaf',
        defaultTitle: 'Wallets',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            void renderOrgWallets(container, ctx);
            return container;
        },
    };
}

async function renderOrgWallets(container: HTMLElement, ctx: PaneTypeRenderContext): Promise<void> {
    container.innerHTML = '';
    const plan = planOrgWalletsView(identity);

    if (plan.kind === 'no-organization') {
        container.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'No organization is active in this session.',
        }));
        return;
    }
    if (plan.kind === 'denied') {
        container.appendChild(createElement('p', { className: 'cid-pane-note', textContent: plan.message }));
        return;
    }

    const group = createFieldGroup({ title: 'Wallets' });
    container.appendChild(group.element);
    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'The organization\'s bitcash wallet pays for the work its delegates do. Your '
            + 'device holds its key through the organization\'s key wraps, so nothing here asks for a '
            + 'supersecret — and revoking a permission here takes it away from every delegate at once.',
    }));

    const body = createElement('div');
    group.body.appendChild(body);

    const render = async (): Promise<void> => {
        body.innerHTML = '';
        const loading = createElement('p', {
            className: 'cid-pane-note', textContent: 'Opening the organization\'s wallet…',
        });
        body.appendChild(loading);

        const walletKey = await getOrgWalletKey(plan.orgCid);
        loading.remove();
        if (!walletKey) {
            body.appendChild(createElement('p', {
                className: 'cid-wizard-warning',
                textContent: 'The organization\'s wallet key is not available to this session. It is '
                    + 'wrapped to your identity\'s encryption key when you are made an owner or a '
                    + 'treasurer; if you have just been given the role, sign in again so your device '
                    + 'picks the wrap up.',
            }));
            return;
        }

        const compact = toCompact(walletKey);
        const slot: WalletSlot = {
            keyIndex: 0,
            key: compact,
            label: `Organization wallet (${plan.orgCid})`,
            source: 'registration',
            createdAt: 0,
        };

        const card = createElement('div', { style: { marginTop: '16px' } });
        body.appendChild(card);
        const worker = liveWalletsWorker();
        await renderWalletCard(card, {
            shell: ctx.shell,
            worker,
            slot,
            title: `Organization wallet · ${plan.orgCid}`,
            // The organization's own cache slot (§4.13): a delegate's personal
            // default token and the organization's must never share one.
            cacheSlot: orgDefaultTokenSlot(identity.getActiveCid() ?? plan.orgCid, plan.orgCid),
            cache: getDefaultTokenCache(),
            // See the module header: publication belongs to the account that
            // owns the claim, and this session is a delegate's.
            canPublish: false,
            // This delegate's own codes on the organization wallet; the others' show by ref (M12, D4).
            surface: 'organization',
            onMintDelegateToken: async (delegateCid, caps, serviceTypes) => {
                await mintDelegateToken({ worker, walletKey: compact, delegateCid, caps, serviceTypes });
            },
            reload: () => { void render(); },
        });
    };

    await render();
}
